import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Box, Button, Checkbox, FormControl, FormErrorMessage, FormLabel, FormHelperText, Input, Textarea, VStack, HStack, Text, Grid, Spinner } from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'

interface BusinessSettingFormProps {
  settingId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type BusinessSettingFormValues = {
  name: string
  key: string
  value: string
  updated_at?: string | null
}

type SlaTimingOption = {
  id: number
  severity: string
  name?: string
}

export function BusinessSettingForm({ settingId, onSuccess, onCancel }: BusinessSettingFormProps) {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [slaOptions, setSlaOptions] = useState<SlaTimingOption[]>([])
  const [selectedSeverityBypassSlas, setSelectedSeverityBypassSlas] = useState<number[]>([])
  const isEditMode = !!settingId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<BusinessSettingFormValues>({
  })

  useEffect(() => {
    const loadSlaOptions = async () => {
      try {
        const { data } = await apiClient.get('/service-catalog/sla-timing', { params: { per_page: 200 } })
        const list = data?.data ?? data ?? []
        const options = Array.isArray(list)
          ? list.map((item: any) => ({ id: item.id, severity: item.severity ?? String(item.id), name: item.name ?? item.severity ?? String(item.id) }))
          : []
        setSlaOptions(options)
      } catch (error) {
        console.error('Failed to load SLA timing options', error)
      }
    }

    loadSlaOptions()
  }, [])

  useEffect(() => {
    if (isEditMode && settingId) {
      setIsLoading(true)
      apiClient
        .get(`/service-catalog/business-settings/${settingId}`)
        .then(({ data }) => {
          const settingData = data?.data || data || {}

          let parentId: number | null = null
          if (settingData.parent_id) {
            parentId = settingData.parent_id
          } else if (settingData.parent_setting?.id) {
            parentId = settingData.parent_setting.id
          }

          const persistedSeverityBypass: number[] = (() => {
            // For severity_bypass settings, parse the value field which should be a JSON array
            const isLoadingSeverityBypass = settingData.key === 'severity_bypass'
            if (isLoadingSeverityBypass && typeof settingData.value === 'string') {
              try {
                const parsed = JSON.parse(settingData.value)
                return Array.isArray(parsed) ? parsed.filter((id: any) => typeof id === 'number') : []
              } catch {
                // ignore parse errors
                return []
              }
            }
            return []
          })()

          setSelectedSeverityBypassSlas(persistedSeverityBypass)

          reset({
            name: settingData.name || '',
            key: settingData.key || '',
            value: settingData.value || '',
            updated_at: settingData.updated_at || null,
          })
        })
        .catch((error: any) => {
          showError(error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [settingId, isEditMode, reset, showError])

  const onSubmit = async (data: BusinessSettingFormValues) => {
    setIsSubmitting(true)

    try {
      const payload: Record<string, any> = {
        name: data.name,
        key: data.key,
        updated_at: data.updated_at,
      }

      // For severity_bypass settings, save the selected SLA IDs as the value (JSON array)
      if (isSeverityBypassSetting) {
        payload.value = JSON.stringify(selectedSeverityBypassSlas)
      } else {
        payload.value = data.value
      }

      if (isEditMode) {
        await apiClient.put(`/service-catalog/business-settings/${settingId}`, payload)
        showSuccess('Success', 'Business setting updated successfully')
      } else {
        await apiClient.post('/service-catalog/business-settings', payload)
        showSuccess('Success', 'Business setting created successfully')
      }

      if (onSuccess) {
        onSuccess()
      } else {
        navigate('/service-catalog/business-settings')
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/service-catalog/business-settings')
    }
  }

  if (isLoading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading business setting...</Text>
      </VStack>
    )
  }

  const settingKey = watch('key')
  const settingName = watch('name')?.toLowerCase().trim()

  const isSeverityBypassSetting = settingKey === 'severity_bypass'
  const isTimeSetting =
  settingKey === 'start_time' || settingKey === 'end_time'

  return (
      <Box as="form" onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing={4} align="stretch">
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Business Setting Name <Text as="span" color="red.500">*</Text>
            </FormLabel>
            <FormControl isInvalid={!!errors.name}>
              <Input {...register('name')} placeholder="e.g. Company Business Hours" />
              <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
              <FormHelperText>The display name for this business setting.</FormHelperText>
            </FormControl>
          </Grid>

          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Business Setting Key <Text as="span" color="red.500">*</Text>
            </FormLabel>
            <FormControl isInvalid={!!errors.key}>
              <Input {...register('key')} placeholder="e.g. Technology, Business, Health" />
              <FormErrorMessage>{errors.key?.message}</FormErrorMessage>
              <FormHelperText>The key is how it appears on your site.</FormHelperText>
            </FormControl>
          </Grid>

        {(!isEditMode || !isSeverityBypassSetting) && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Business Setting Value <Text as="span" color="red.500">*</Text>
              </FormLabel>
              <FormControl isInvalid={!!errors.value}>
                <Input
                  {...register('value')}
                  type="time"
                  placeholder="HH:MM"
                  step={60}
                />
                <FormErrorMessage>{errors.value?.message}</FormErrorMessage>
                <FormHelperText>The value should be a time in HH:MM format.</FormHelperText>
              </FormControl>
          </Grid>
        )}

        {isEditMode && isSeverityBypassSetting && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
              <FormLabel pt={2} fontWeight="medium">
                Severity Bypass <Text as="span" color="red.500">*</Text>
              </FormLabel>
              <FormControl isInvalid={!!errors.value}>
                <HStack spacing={10} align="center" wrap="wrap">
                  {slaOptions.length > 0 ? (
                    slaOptions.map((sla) => {
                      const label = sla.name ?? `SLA ${sla.id}`
                      return (
                        <Checkbox
                          key={sla.id}
                          isChecked={selectedSeverityBypassSlas.includes(sla.id)}
                          onChange={(event) => {
                            setSelectedSeverityBypassSlas((prev) =>
                              event.target.checked
                                ? [...prev, sla.id]
                                : prev.filter((id) => id !== sla.id)
                            )
                          }}
                        >
                          Severity {sla.id}
                        </Checkbox>
                      )
                    })
                  ) : (
                    <Text color="gray.500">Loading SLA options…</Text>
                  )}
                </HStack>
              </FormControl>
          </Grid>
        )}

          <HStack spacing="4" justify="flex-end" pt={4}>
            <Button variant="outline" onClick={handleCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={isSubmitting}
              loadingText={isEditMode ? 'Saving...' : 'Creating...'}
            >
              {isEditMode ? 'Save' : 'Create Business Setting'}
              {isSubmitting && (
                <Text as="span" ml={2} fontSize="sm">
                  ...
                </Text>
              )}
            </Button>
          </HStack>
        </VStack>
      </Box>
    )
}